import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

// ScrollProgress: Thin progress bar at the top indicating scroll percentage
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const handler = () => {
      const total = document.body.scrollHeight - window.innerHeight;
      setProgress((window.scrollY / total) * 100);
    };
    window.addEventListener("scroll", handler);
    return () => window.removeEventListener("scroll", handler);
  }, []);
  return (
    <div className="fixed top-0 left-0 w-full h-1 bg-blue-200 z-50">
      <div
        className="h-full bg-blue-600 transition-all duration-200"
        style={{ width: `${progress}%` }}
      />
    </div>
  );
};

// SectionCard: Animated card wrapper for each section (h3 + content)
const SectionCard = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    whileHover={{ scale: 1.03, y: -5 }}
    transition={{
      duration: 0.4,
      ease: "easeOut",
      type: "spring",
      stiffness: 200,
      damping: 20,
    }}
    viewport={{ once: true, amount: 0.2 }}
    className="flex-1 p-6 bg-white/80 backdrop-blur-sm shadow-lg border border-blue-100 rounded"
  >
    <h3 className="text-xl font-bold text-blue-900 mb-3">{title}</h3>
    {children}
  </motion.div>
);

// Subheading: Style for subheading text inside cards
const Subheading = ({ children }: { children: React.ReactNode }) => (
  <h4 className="text-red-700 font-semibold text-xl mt-2.5 mb-1">{children}</h4>
);

// Paragraph: Styled paragraph for consistent text appearance
const Paragraph = ({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <p
    className={`text-lg text-gray-800 leading-relaxed text-justify ${className}`}
  >
    {children}
  </p>
);

const BurnMintMechanism = () => (
  <div className="relative overflow-hidden">
    {/* Render scroll progress bar and background animation */}
    <ScrollProgress />

    <div className="space-y-8 px-4 py-10 md:py-6 md:px-8 text-justify">
      {/* Introduction: Animated into view */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{
          duration: 0.5,
          ease: "easeOut",
          type: "spring",
          stiffness: 200,
          damping: 20,
        }}
        viewport={{ once: true }}
        className="max-w-6xl mx-auto space-y-4 text-center"
      >
        <h2 className="text-3xl font-bold text-primary-dark">
          Burning and Minting Mechanism in Uncensored Waves
        </h2>
        <Paragraph className="text-gray-700 max-w-6xl mx-auto text-base sm:text-lg md:text-lg text-justify">
          At the heart of Uncensored Waves’ governance token economy lies a
          robust burn-and-mint process: users surrender (burn) the base token
          NAU, and in return receive measured allocations of two opinion
          tokens—NAUY (“yes”) and NAUN (“no”). The following explains how the
          mechanism works, the security guarantees it provides, and the critical
          role of a time-weighted average price (TWAP) oracle called on-chain by
          the Controller contract to ensure trustless, manipulation-resistant
          minting proportions.
        </Paragraph>
      </motion.div>

      {/* Two-column layout for the main sections */}
      <div className="flex flex-col md:flex-row gap-6 max-w-6xl mx-auto">
        {/* Section 1: Overview of the burn-and-mint flow */}
        <SectionCard title="1. Overview of the Burn-and-Mint Flow">
          <ul className="list-disc list-inside text-lg text-gray-800 mb-2">
            <li>
              <strong>Burn NAU:</strong> A user invokes the{" "}
              <code>transformX()</code> function on the Controller contract,
              specifying an amount of NAU to burn.
            </li>
            <br />
            <li>
              <strong>Fetch TWAP:</strong> The Controller contract calls Uniswap
              V3’s built-in oracle library to obtain a recent TWAP between NAUY
              and NAUN pools, smoothing out short-term volatility.
            </li>
            <br />
            <li>
              <strong>Compute Proportions:</strong> Using the TWAP-derived price
              ratio, the contract calculates how many NAUY and NAUN tokens
              should be minted per NAU burned. For a detailed explanation, refer
              to the section below
              <Link
                to="#fair-burn"
                onClick={(e) => {
                  e.preventDefault();
                  const el = document.getElementById("fair-burn");
                  if (el) {
                    el.scrollIntoView({ behavior: "smooth" });
                  }
                }}
                className="text-red-700 underline font-semibold"
              >
                "Fair Burn"
              </Link>
              .
            </li>
            <br />
            <li>
              <strong>Mint Opinion Tokens:</strong> The contract mints the
              calculated amounts of NAUY and NAUN directly to the user’s
              address, completing the one-way conversion.
            </li>
          </ul>
        </SectionCard>

        {/* Section 2: Security guarantees of the burn-and-mint mechanism */}
        <SectionCard title="2. Security Guarantees">
          <Subheading>2.1 Atomicity and Trustlessness</Subheading>
          <ul className="list-disc list-inside text-lg text-gray-800 mb-2">
            <li>
              <strong>Single-Transaction Execution:</strong> By bundling
              burning, TWAP retrieval, and minting in one on-chain call, there
              is no window for asset misplacement or inconsistent state. Either
              all steps succeed or the transaction reverts.
            </li>
            <br />
            <li>
              <strong>Immutable Contract Logic:</strong> The Controller’s logic
              is fixed on deployment. No off-chain agent or privileged role can
              alter the burn- and-mint ratios or bypass the TWAP call.
            </li>
          </ul>

          <Subheading>2.2 One-Way Conversion</Subheading>
          <ul className="list-disc list-inside text-lg text-gray-800 mb-2">
            <li>
              <strong>NAU as the Sole Input:</strong> NAUY and NAUN cannot be
              minted except by burning NAU. This prevents inflation of opinion
              tokens and preserves a clear supply relationship.
            </li>
            <br />
            <li>
              <strong>No Direct Minting:</strong> The mint function for
              NAUY/NAUN is internal and only callable by the Controller’s{" "}
              <code>transformX()</code>, ensuring no external contract or user
              can create tokens arbitrarily.
            </li>
          </ul>

          <Subheading>2.3 Access Control</Subheading>
          <ul className="list-disc list-inside text-lg text-gray-800">
            <li>
              <strong>Controller-Only Oracle Access:</strong> Only the
              Controller contract holds the privilege to call Uniswap’s oracle
              functions. Users cannot spoof or bypass the oracle to influence
              outcomes.
            </li>
            <br />
            <li>
              <strong>Role Restrictions:</strong> If additional governance or
              administrative roles exist, they cannot interfere with the core
              burn- and-mint flow once the Controller is locked.
            </li>
          </ul>
        </SectionCard>
      </div>
      <Paragraph>
        This atomic, single-transaction process ensures that burning and minting
        occur together without intermediate states where assets are unaccounted
        for.
      </Paragraph>
    </div>
  </div>
);

export default BurnMintMechanism;
